import React from 'react';
import { useShop } from '../contexts/ShopContext';

/**
 * Quick "add to collection" control on a slide card, positioned beside the FavoriteButton (bottom
 * right of the image). Opens SlideCollectionsPickerModal via `onClick`; the CARD renders the modal
 * (outside its clickable image area, next to SlideViewModal) so the modal can never trigger the
 * card's open-detail or photo-swipe handlers. Stops propagation for the same reason.
 *
 * Shown on hover on devices that hover (pass `visible`), always on touch devices.
 * Hidden for external (CLIENT/VENDOR) members, who have no collections, like FavoriteButton.
 */
interface AddToCollectionButtonProps {
  onClick: () => void;
  /** Hover state from the card; ignored on touch devices, where the button is always shown. */
  visible?: boolean;
  size?: number;
  /** Distance from the right edge, so it sits just left of the favorite star. */
  right?: number;
  bottom?: number;
}

const canHover = (): boolean => {
  try {
    return window.matchMedia('(hover: hover)').matches;
  } catch {
    return false;
  }
};

const AddToCollectionButton: React.FC<AddToCollectionButtonProps> = ({
  onClick, visible = true, size = 24, right = 44, bottom = 10,
}) => {
  const { isInternalMember } = useShop();
  if (!isInternalMember) return null;

  const shown = visible || !canHover();

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      aria-label="Add to collection"
      title="Add to collection"
      style={{
        position: 'absolute', bottom: `${bottom}px`, right: `${right}px`,
        background: 'transparent', border: 'none', padding: 4, margin: 0,
        cursor: 'pointer', zIndex: 4, lineHeight: 0,
        opacity: shown ? 1 : 0, transition: 'opacity 0.15s ease',
        // Keep it keyboard-reachable even while hidden: focus reveals it.
      }}
      onFocus={(e) => { e.currentTarget.style.opacity = '1'; }}
      onBlur={(e) => { e.currentTarget.style.opacity = shown ? '1' : '0'; }}
    >
      {/* Lucide "copy-plus" (stacked cards + plus), white stroke + shadow like the favorite star. */}
      <svg
        width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }}
      >
        <line x1="15" x2="15" y1="12" y2="18" />
        <line x1="12" x2="18" y1="15" y2="15" />
        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
      </svg>
    </button>
  );
};

export default AddToCollectionButton;
